import { Fragment, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@rtkwlf/pro-solid-svg-icons/faBell';
import { faXmark } from '@rtkwlf/pro-solid-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Divider,
  IconButton,
  Menu,
  Stack,
  Typography,
} from '@packages/fenrir/components';

import type { FeatureRelease } from '../types';

import { useNewsItems } from './use-news-items/use-news-items';
import { useUnreadCount } from './use-unread-count/use-unread-count';
import { WhatsNewMenuItem } from './whats-new-menu-item/whats-new-menu-item';

export type WhatsNewProps = {
  featureReleases: FeatureRelease[];
};

export const WhatsNew = ({ featureReleases }: WhatsNewProps) => {
  const { t } = useTranslation('whatsNew');

  const { unreadCount, markAllRead } = useUnreadCount(featureReleases);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    markAllRead();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const newsItems = useNewsItems(featureReleases);

  return (
    <>
      <IconButton
        size="small"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        data-testid="whats-new-open-button"
        onClick={handleOpen}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <FontAwesomeIcon icon={faBell} size="sm" />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
      >
        <Stack sx={{ width: 500 }} gap={3}>
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ paddingX: 2, paddingY: 1 }}
              gap={5}
            >
              <Typography variant="h4">{t('title')}</Typography>
              <IconButton
                size="small"
                data-testid="whats-new-close-button"
                onClick={handleClose}
              >
                <FontAwesomeIcon icon={faXmark} size="sm" />
              </IconButton>
            </Stack>
            <Divider variant="middle" />
          </Stack>
          <Stack gap={1}>
            {newsItems.map(({ title, description, url, date }, index) => {
              const isLastItem = index === newsItems.length - 1;
              return (
                <Fragment key={title}>
                  <WhatsNewMenuItem
                    title={title}
                    description={description}
                    url={url}
                    date={date}
                  />
                  {!isLastItem && <Divider variant="middle" />}
                </Fragment>
              );
            })}
          </Stack>
        </Stack>
      </Menu>
    </>
  );
};
